import { useState, useCallback, useEffect } from 'react';
import type { DayData, DayStatus, HourData, PublicProbesResponse } from '../types.ts';
import { ProbeDetailDrawer } from './ProbeDetailDrawer.tsx';
import {
  STATUS_LEGEND,
  STATUS_CSS_VAR,
  STATUS_LABEL,
  LEGEND_NOTAS,
} from './statusLegend.ts';

interface Props {
  historico: DayData[];
  historicoHoras: HourData[];
  fetchProbesFn?: (from: string, to: string) => Promise<PublicProbesResponse>;
  /**
   * Só a página interna (admin) passa isto: habilita o toggle Real / Contratual no cabeçalho e faz a
   * legenda descrever a visão ativa. Ausente (página pública) → sempre contratual, sem toggle.
   */
  modeToggle?: { raw: boolean; onChange: (raw: boolean) => void };
  /**
   * Só admin: checkbox "Descontar manutenções", visível apenas no modo Real (o Contratual já
   * desconta manutenção). Recalcula os cards no backend.
   */
  maintenanceToggle?: { exclude: boolean; onChange: (exclude: boolean) => void };
}

type ViewMode = 'days' | 'hours';

interface SelectedPeriod {
  from: string;
  to: string;
  label: string;
}

const fmtDate = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const fmtHour = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export function StatusBars({ historico, historicoHoras, fetchProbesFn, modeToggle, maintenanceToggle }: Props) {
  const [tooltip, setTooltip] = useState<{ idx: number; x: number; y: number } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('days');
  const [selected, setSelected] = useState<SelectedPeriod | null>(null);
  const [statusExplicado, setStatusExplicado] = useState<DayStatus | null>(null);

  // Esc fecha o painel da legenda
  useEffect(() => {
    if (!statusExplicado) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStatusExplicado(null);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [statusExplicado]);

  const itemExplicado = STATUS_LEGEND.find((i) => i.status === statusExplicado) ?? null;

  const daysUp = historico.filter((d) => d.status === 'up').length;
  const hoursUp = historicoHoras.filter((h) => h.status === 'up').length;

  const items = viewMode === 'days' ? historico : historicoHoras;

  const handleBarClick = useCallback((idx: number) => {
    const item = items[idx];
    if (!item || item.status === 'no-data') return;

    if (viewMode === 'days') {
      const day = item as DayData;
      const from = day.date + 'T00:00:00.000Z';
      const to = day.date + 'T23:59:59.999Z';
      const label = fmtDate.format(new Date(day.date + 'T12:00:00'));
      setSelected({ from, to, label });
    } else {
      const hour = item as HourData;
      const hourDate = new Date(hour.hour);
      const nextHour = new Date(hourDate.getTime() + 60 * 60 * 1000);
      const from = hourDate.toISOString();
      const to = nextHour.toISOString();
      const label = fmtHour.format(hourDate);
      setSelected({ from, to, label });
    }
  }, [items, viewMode]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {viewMode === 'days' ? 'Últimos 90 dias' : 'Últimas 24 horas'}
          </h2>
          <div className="flex rounded-md border border-gray-200 text-xs dark:border-gray-700">
            <button
              onClick={() => { setViewMode('days'); setTooltip(null); }}
              className={`px-2.5 py-1 rounded-l-md transition-colors ${
                viewMode === 'days'
                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              }`}
            >
              Dias
            </button>
            <button
              onClick={() => { setViewMode('hours'); setTooltip(null); }}
              className={`px-2.5 py-1 rounded-r-md transition-colors ${
                viewMode === 'hours'
                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              }`}
            >
              Horas
            </button>
          </div>

          {/* Toggle Real / Contratual — só no admin (via modeToggle) */}
          {modeToggle && (
            <div className="flex rounded-md border border-gray-200 text-xs dark:border-gray-700">
              <button
                onClick={() => modeToggle.onChange(true)}
                className={`px-2.5 py-1 rounded-l-md transition-colors ${
                  modeToggle.raw
                    ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                }`}
              >
                Real
              </button>
              <button
                onClick={() => modeToggle.onChange(false)}
                className={`px-2.5 py-1 rounded-r-md transition-colors ${
                  !modeToggle.raw
                    ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                }`}
              >
                Contratual
              </button>
            </div>
          )}

          {/* Descontar manutenções — só no modo Real (Contratual já desconta) */}
          {modeToggle?.raw && maintenanceToggle && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <input
                type="checkbox"
                checked={maintenanceToggle.exclude}
                onChange={(e) => maintenanceToggle.onChange(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600"
              />
              Descontar manutenções
            </label>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {viewMode === 'days'
            ? `${daysUp} de ${historico.length} dias operacionais`
            : `${hoursUp} de ${historicoHoras.length} horas operacionais`}
        </span>
      </div>

      {/* Barras */}
      <div
        className="relative flex items-end gap-[2px]"
        onMouseLeave={() => setTooltip(null)}
      >
        {items.map((item, idx) => (
          <div
            key={viewMode === 'days' ? (item as DayData).date : (item as HourData).hour}
            className="flex-1 cursor-pointer rounded-sm transition-opacity hover:opacity-80"
            style={{
              height: '32px',
              backgroundColor: STATUS_CSS_VAR[item.status],
            }}
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltip({ idx, x: rect.left + rect.width / 2, y: rect.top });
            }}
            onClick={() => handleBarClick(idx)}
          />
        ))}

        {/* Tooltip */}
        {tooltip !== null && items[tooltip.idx] && (
          <div
            className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-800"
            style={{ left: tooltip.x, top: tooltip.y - 8 }}
          >
            <p className="font-medium text-gray-900 dark:text-white">
              {viewMode === 'days'
                ? fmtDate.format(new Date((items[tooltip.idx] as DayData).date + 'T12:00:00'))
                : fmtHour.format(new Date((items[tooltip.idx] as HourData).hour))}
            </p>
            <p style={{ color: STATUS_CSS_VAR[items[tooltip.idx].status] }}>
              {STATUS_LABEL[items[tooltip.idx].status]}
            </p>
            {viewMode === 'days' && (items[tooltip.idx] as DayData).downtimeMin != null && (items[tooltip.idx] as DayData).downtimeMin! > 0 && (
              <p className="text-gray-500 dark:text-gray-400">
                {/* No modo Real o backend devolve CONTAGEM de amostras DOWN, não minutos —
                    rotular como "min" superestimaria (1 amostra ≈ até 1 intervalo de probe). */}
                {modeToggle?.raw
                  ? `${(items[tooltip.idx] as DayData).downtimeMin} check${(items[tooltip.idx] as DayData).downtimeMin! > 1 ? 's' : ''} com falha`
                  : `${(items[tooltip.idx] as DayData).downtimeMin} min indisponível`}
              </p>
            )}
            {viewMode === 'hours' && (items[tooltip.idx] as HourData).downChecks != null && (items[tooltip.idx] as HourData).downChecks! > 0 && (
              <p className="text-gray-500 dark:text-gray-400">
                {(items[tooltip.idx] as HourData).downChecks} checks com falha
              </p>
            )}
            {items[tooltip.idx].uptimePct != null && (
              <p className="text-gray-500 dark:text-gray-400">
                {items[tooltip.idx].uptimePct!.toFixed(2)}% uptime
              </p>
            )}
            {items[tooltip.idx].status !== 'no-data' && (
              <p className="mt-1 text-gray-400 dark:text-gray-500">
                Clique para ver probes
              </p>
            )}
          </div>
        )}
      </div>

      {/* Legenda — cada item abre a explicação abaixo */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
        {STATUS_LEGEND.map(({ status, label, cssVar }) => {
          const ativo = statusExplicado === status;
          return (
            <button
              key={status}
              type="button"
              aria-expanded={ativo}
              aria-controls="legenda-explicacao"
              onClick={() => setStatusExplicado(ativo ? null : status)}
              className={`flex items-center gap-1.5 rounded px-1.5 py-0.5 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-800 dark:hover:text-gray-200 ${
                ativo
                  ? 'bg-gray-100 font-medium text-gray-900 dark:bg-gray-800 dark:text-white'
                  : ''
              }`}
            >
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                style={{ backgroundColor: cssVar }}
              />
              {label}
            </button>
          );
        })}
      </div>

      {/* Explicação do status selecionado */}
      <div id="legenda-explicacao" role="region" aria-live="polite">
        {itemExplicado && (
          <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <span
                  aria-hidden="true"
                  className="mt-1 inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                  style={{ backgroundColor: itemExplicado.cssVar }}
                />
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    {itemExplicado.label}
                  </p>
                  <p className="text-xs leading-snug text-gray-600 dark:text-gray-300">
                    {modeToggle?.raw
                      ? itemExplicado.descricaoRaw ?? itemExplicado.descricao
                      : itemExplicado.descricao}
                  </p>
                  {(itemExplicado.status === 'up' ||
                    itemExplicado.status === 'down' ||
                    itemExplicado.status === 'partial') && (
                    <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                      {LEGEND_NOTAS.respostaIncluiErro}
                    </p>
                  )}
                  {itemExplicado.status !== 'no-data' && (
                    <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                      {LEGEND_NOTAS.cliqueNaBarra}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStatusExplicado(null)}
                aria-label="Fechar explicação"
                className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path
                    d="M3.5 3.5l7 7m0-7l-7 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer de detalhes */}
      {selected && (
        <ProbeDetailDrawer
          from={selected.from}
          to={selected.to}
          label={selected.label}
          onClose={() => setSelected(null)}
          fetchProbesFn={fetchProbesFn}
        />
      )}
    </div>
  );
}
